"use client";
import { Close, Done, Error, Warning } from "@mui/icons-material";
import { toast as sonnerToast } from "sonner";
import { cn } from "../../../common/utils/cn";
import { ToastNotificationProps } from "../../../types/ToastNotification";
import { Button } from "@mui/material";
import { useEffect, useState } from "react";

export function toast(toast: Omit<ToastNotificationProps, "id">) {
  return sonnerToast.custom(
    (id) => (
      <CustomToast
        id={id}
        title={toast.title}
        description={toast.description}
        variant={toast.variant || "error"}
        onClose={toast.onClose}
        action={toast.action}
        closeButton={toast.closeButton ?? true}
        hasDurationCountdown={toast.hasDurationCountdown || false}
        duration={toast.duration || 4000}
      />
    ),
    {
      position:
        document?.documentElement?.dir === "rtl" ? "top-left" : "top-right",
      duration: toast.duration || 4000,
    }
  );
}

export function CustomToast({
  id,
  title,
  description,
  variant,
  action,
  closeButton,
  hasDurationCountdown,
  duration,
}: ToastNotificationProps) {
  const [remainingTime, setRemainingTime] = useState(duration);

  useEffect(() => {
    if (hasDurationCountdown) {
      const interval = setInterval(() => {
        setRemainingTime((prev) => {
          const newTime = (prev ?? 0) - 1000;
          return newTime > 0 ? newTime : 0;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [duration, hasDurationCountdown]);

  return (
    <div
      className={cn(
        "flex w-full items-center p-4 shadow-lg md:!w-[500px]",
        variant === "error"
          ? "bg-red-200"
          : variant === "warning"
          ? "bg-yellow-200"
          : "bg-green-200"
      )}
    >
      <div className="flex flex-1 items-center">
        <div className="me-4">
          {variant === "error" ? (
            <Error className="h-[32px] w-[32px]" />
          ) : variant === "warning" ? (
            <Warning className="h-[32px] w-[32px]" />
          ) : (
            <Done className="h-[32px] w-[32px]" />
          )}
        </div>

        <div className="w-full">
          <p className="text-sm font-medium text-black line-clamp-2 text-ellipsis">
            {title}
          </p>
          <p className="mt-1 text-sm text-primary-grey-400 line-clamp-2 text-ellipsis">
            {description}
            {hasDurationCountdown &&
              remainingTime &&
              remainingTime > 0 &&
              ` (${Math.ceil(remainingTime / 1000)}s)`}
          </p>
        </div>
      </div>
      <div className="ml-5 shrink-0">
        {action && (
          <Button
            onClick={(e: any) => {
              action.onClick(e);
              sonnerToast.dismiss(id);
            }}
            variant="contained"
            className="!py-2 text-sm !px-3 !capitalize !rounded-lg"
            color="success"
          >
            {action.label}
          </Button>
        )}
        {closeButton && (
          <button
            className="bg-transparent"
            onClick={() => {
              sonnerToast.dismiss(id);
            }}
          >
            <Close className="[&>path]:stroke-navy-primary" />
          </button>
        )}
      </div>
    </div>
  );
}

export function dismissToast(toastId: string | number) {
  sonnerToast.dismiss(toastId);
}
